import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { LegalDisclaimer } from "../components/LegalDisclaimer";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import {
  Lightning,
  CheckCircle,
  LockKey,
  Sparkle,
  Compass,
  FileText,
  Users,
  ArrowRight,
  Crown,
} from "@phosphor-icons/react";

/**
 * FormationToolkit — Premium-tier 501(c)(3) Formation Toolkit hub.
 * Module 01 (Mission Statement Diagnostic) is LIVE.
 * Modules 01–02 shipped. Module 03 (Bylaws) in build — August 2026.
 * Modules 04 (Filings Tracker) and 05 (Board Recruiting) intentionally REMOVED from this
 * toolkit on Jul 11, 2026 — they serve post-formation founders (Year 1 Ops), not the
 * formation pathway itself. When those modules ship they'll live on their own product
 * surface, not squatting here as vaporware.
 */

const MODULES = [
  {
    id: "mission-diagnostic",
    eyebrow: "Module 01",
    icon: Compass,
    title: "Mission Statement Diagnostic",
    summary:
      "Paste your draft mission. Get a 0–100 score on clarity, impact verbs, specificity, and funder readiness — plus 3 concrete rewrites tuned to SC funders.",
    cta: "Run a diagnostic",
    path: "/formation/mission-diagnostic",
    estimatedMinutes: 5,
    status: "ready",
  },
  {
    id: "1023ez-walkthrough",
    eyebrow: "Module 02",
    icon: FileText,
    title: "IRS Form 1023-EZ Walkthrough",
    summary:
      "Step-by-step interactive guide through every question on the 1023-EZ — with plain-English explanations, common-mistake flags, and an IRS-aligned eligibility quiz to catch problems before you pay the $275 fee.",
    cta: "Start the walkthrough",
    path: "/formation/1023-ez",
    estimatedMinutes: 45,
    status: "ready",
  },
  {
    id: "bylaws-templates",
    eyebrow: "Module 03",
    icon: Users,
    title: "Bylaws & Governance Templates",
    summary:
      "SC-specific curated resource: IRS-required bylaws elements checklist, SC Nonprofit Corporation Act customization guide, direct links to the exact IRS-published conflict-of-interest, document retention, and whistleblower policy templates. Copy-ready bylaws outline included.",
    cta: "Open the resource",
    path: "/formation/bylaws-templates",
    estimatedMinutes: 20,
    status: "ready",
  },
];

const STATUS_BADGES = {
  ready: { label: "Ready", className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  "coming-soon": { label: "Coming Soon", className: "bg-zinc-800 text-zinc-400 border-zinc-700" },
  "in-progress": { label: "In Build — August", className: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  "complete": { label: "Complete", className: "bg-emerald-500/30 text-emerald-200 border-emerald-500/60" },
};

const FormationToolkit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPremium = user?.subscription_tier === "pro" || user?.role === "admin" || ["trialing", "active"].includes(user?.trial_status);
  const showLocked = !isPremium;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="formation-toolkit-page">
        {/* Hero */}
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] font-bold uppercase tracking-[0.2em] mb-5">
            <Crown weight="fill" className="w-3.5 h-3.5" />
            Premium · 501(c)(3) Formation Toolkit
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white leading-tight mb-4">
            Form your nonprofit.
            <br />
            <span className="text-emerald-400">Govern it like a pro.</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed">
            Everything a SC founder needs to incorporate a 501(c)(3) and run it
            cleanly — without spending $1,500–$3,000 on a consultant. Built with
            South Carolina nonprofit founders, for South Carolina nonprofit founders.
          </p>
        </div>

        {/* Order to File — helps founders understand the real sequence */}
        <Card className="bg-zinc-950 border-zinc-800 mb-8" data-testid="order-to-file-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <Compass weight="fill" className="shrink-0 w-6 h-6 text-emerald-400 mt-1" />
              <div>
                <h3 className="text-white font-bold text-lg mb-2">The order to file — start here</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Forming a 501(c)(3) has a specific sequence. The modules below cover the
                  mission, federal, and governance work. The state filings happen first —
                  see our SC-specific roadmap for those.
                </p>
              </div>
            </div>
            <ol className="space-y-2 ml-9 text-sm text-zinc-300">
              <li className="flex gap-3">
                <span className="text-emerald-400 font-bold w-6 shrink-0">1.</span>
                <span>
                  <Link to="/start-a-nonprofit-in-sc" className="text-emerald-400 hover:underline" data-testid="order-step-sc-articles">
                    File SC Articles of Incorporation
                  </Link>{" "}
                  <span className="text-zinc-500">(state filing — SC Secretary of State, ~$25)</span>
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-emerald-400 font-bold w-6 shrink-0">2.</span>
                <span>
                  Get your EIN <span className="text-zinc-500">(federal, free, ~10 minutes on IRS.gov)</span>
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-emerald-400 font-bold w-6 shrink-0">3.</span>
                <span>
                  Clarify your mission <span className="text-zinc-500">(Module 01 below)</span>
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-emerald-400 font-bold w-6 shrink-0">4.</span>
                <span>
                  Draft your bylaws <span className="text-zinc-500">(Module 03 below)</span>
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-emerald-400 font-bold w-6 shrink-0">5.</span>
                <span>
                  File IRS Form 1023-EZ <span className="text-zinc-500">(Module 02 below, $275 filing fee)</span>
                </span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Locked state (only for non-Premium) */}
        {showLocked && (
          <Card className="bg-zinc-950 border-emerald-500/30 mb-10 overflow-hidden" data-testid="formation-toolkit-locked-card">
            <CardContent className="p-0">
              <div className="grid md:grid-cols-[1fr_auto] gap-6 p-8 items-center">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <LockKey weight="fill" className="w-5 h-5 text-emerald-400" />
                    <span className="text-emerald-300 font-bold uppercase tracking-wider text-xs">
                      Unlock with FlashGrants Premium
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    $39.99 / month gets you the full toolkit + every grant feature.
                  </h2>
                  <p className="text-zinc-400 leading-relaxed mb-4">
                    Premium gets you grant matching, Flash Drafts, Corporate Scout,
                    Post-Grant Manager, <i>and</i> the entire Formation Toolkit. The
                    consultant route costs $1,500–$3,000 once. Premium pays for
                    itself in the first month and stays useful long after your
                    determination letter arrives.
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge className="bg-zinc-900 text-zinc-300 border-zinc-700">No long-term commitment</Badge>
                    <Badge className="bg-zinc-900 text-zinc-300 border-zinc-700">Cancel anytime</Badge>
                    <Badge className="bg-zinc-900 text-zinc-300 border-zinc-700">Founding rate locked forever</Badge>
                  </div>
                </div>
                <div className="flex flex-col gap-3 md:items-end">
                  <Link to="/pricing">
                    <Button
                      className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-6 py-6 text-base"
                      data-testid="unlock-premium-btn"
                    >
                      Upgrade to Premium
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/start-a-nonprofit-in-sc" className="text-zinc-500 text-xs hover:text-emerald-400 text-right">
                    Or read the free roadmap →
                  </Link>
                </div>
              </div>
              {/* Quick value summary strip */}
              <div className="grid grid-cols-2 md:grid-cols-5 border-t border-zinc-900">
                {[
                  ["$1,500+", "Saved vs consultant"],
                  ["3", "Modules"],
                  ["~95 min", "End-to-end work"],
                  ["3-6 mo", "Realistic timeline"],
                  ["SC-specific", "Every step"],
                ].map(([big, small]) => (
                  <div key={small} className="px-5 py-4 border-r border-zinc-900 last:border-r-0">
                    <div className="text-emerald-400 font-bold text-lg">{big}</div>
                    <div className="text-zinc-500 text-xs uppercase tracking-wider mt-0.5">{small}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress overview — only visible when unlocked */}
        {!showLocked && (
          <Card className="bg-zinc-950 border-zinc-800 mb-8" data-testid="formation-progress-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Your formation progress</h2>
                <span className="text-zinc-500 text-sm">0 of 3 modules complete</span>
              </div>
              <div className="w-full bg-zinc-900 rounded-full h-2 mb-2">
                <div className="bg-emerald-500 h-2 rounded-full" style={{ width: "0%" }} />
              </div>
              <p className="text-zinc-500 text-xs">
                Start with <b className="text-zinc-300">Module 01 — Mission Statement Diagnostic</b>. The clearer your
                mission, the easier every downstream filing (1023-EZ, bylaws) becomes.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Module grid */}
        <div className="space-y-4">
          {MODULES.map((m) => {
            const Icon = m.icon;
            const badge = STATUS_BADGES[m.status];
            const isClickable = !showLocked && m.status === "ready";
            return (
              <Card
                key={m.id}
                onClick={() => { if (isClickable && m.path) navigate(m.path); }}
                className={`bg-zinc-950 border-zinc-800 transition ${
                  isClickable ? "hover:border-emerald-500/40 cursor-pointer" : ""
                } ${showLocked ? "opacity-70" : ""}`}
                data-testid={`module-card-${m.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-5">
                    <div
                      className={`shrink-0 w-14 h-14 rounded-lg flex items-center justify-center ${
                        m.status === "ready"
                          ? "bg-emerald-500/15 border border-emerald-500/40"
                          : "bg-zinc-900 border border-zinc-800"
                      }`}
                    >
                      <Icon
                        weight="duotone"
                        className={`w-7 h-7 ${m.status === "ready" ? "text-emerald-400" : "text-zinc-500"}`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-zinc-500 text-[11px] font-bold uppercase tracking-[0.18em]">
                          {m.eyebrow}
                        </span>
                        <Badge className={`${badge.className} text-[10px] uppercase tracking-wider font-semibold`}>
                          {badge.label}
                        </Badge>
                        <span className="text-zinc-600 text-xs">· ~{m.estimatedMinutes} min</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{m.title}</h3>
                      <p className="text-zinc-400 leading-relaxed">{m.summary}</p>
                    </div>
                    <div className="hidden md:flex shrink-0 self-center">
                      {showLocked ? (
                        <LockKey weight="fill" className="w-6 h-6 text-zinc-700" />
                      ) : m.status === "ready" ? (
                        <Button
                          variant="outline"
                          className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                          data-testid={`module-cta-${m.id}`}
                        >
                          {m.cta}
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      ) : (
                        <span className="text-zinc-600 text-xs italic">Coming soon</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Trust + Yvonne principle anchor */}
        <Card className="bg-gradient-to-br from-emerald-950/40 to-black border-emerald-500/30 mt-10">
          <CardContent className="p-7">
            <div className="flex items-start gap-4">
              <Lightning weight="fill" className="shrink-0 w-7 h-7 text-emerald-400 mt-1" />
              <div>
                <h3 className="text-white font-bold text-lg mb-2">
                  Built on the same standard our Founding Members get.
                </h3>
                <p className="text-zinc-300 leading-relaxed mb-2">
                  Yvonne D. Small Campbell — our first Founding Premium member —
                  joined pre-501(c)(3) and is using this toolkit alongside her grant
                  matching to file her own paperwork. <b className="text-emerald-300">Premium has always meant
                  the highest tier of value at FlashGrants</b>, and the toolkit is part of that
                  commitment.
                </p>
                <p className="text-zinc-500 text-sm">
                  Every template, walkthrough, and checklist has been reviewed
                  against current IRS Form 1023 governance requirements and SC Secretary
                  of State filing rules.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <LegalDisclaimer variant="footer" />
      </div>
    </DashboardLayout>
  );
};

export default FormationToolkit;
